import * as React from "react";
import { useCallback, useMemo, useContext, useLayoutEffect } from "react";
import camelCase from "lodash/camelCase";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { styled, css } from "styled-components";
import DragHandle from "components/common/SortableList/DragHandle";
import DndStylesContext from "components/common/EntityDataTable/contexts/DndStylesContext";
import useHeaderSectionObserver from "components/common/EntityDataTable/hooks/useHeaderSectionObserver";
import ResizeHandle from "components/common/EntityDataTable/ResizeHandle";
import HeaderActionsDropdown from "components/common/EntityDataTable/HeaderActionsDropdown";
import Icon from "components/common/Icon";
import ActiveSliceColContext from "components/common/EntityDataTable/contexts/ActiveSliceColContext";
import SortIcon from "../SortIcon";
export const ThInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
`;
export const LeftCol = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
const RightCol = styled.div`
  display: flex;
  align-items: center;
`;
const ActiveSliceIcon = styled(Icon)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xs};
    color: ${theme.colors.gray[20]};
  `
);
const useSortableCol = (colId, disabled) => {
  const { setColumnTransform } = useContext(DndStylesContext);
  const { attributes, isDragging, listeners, setNodeRef, transform, setActivatorNodeRef } = useSortable({
    id: colId,
    disabled
  });
  const cssTransform = CSS.Translate.toString(transform);
  useLayoutEffect(() => {
    setColumnTransform((cur) => {
      if (cur[colId] === cssTransform) return cur;
      return { ...cur, [colId]: cssTransform };
    });
  }, [colId, setColumnTransform, cssTransform]);
  return {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef
  };
};
const AttributeHeader = ({
  ctx,
  onHeaderSectionResize,
  onChangeSlicing,
  appSection
}) => {
  var _a, _b, _c, _d;
  const activeSliceCol = useContext(ActiveSliceColContext);
  const colId = ctx.header.column.id;
  const columnMeta = ctx.column.columnDef.meta;
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef } = useSortableCol(
    colId,
    !(columnMeta == null ? void 0 : columnMeta.enableColumnOrdering)
  );
  const leftRef = useHeaderSectionObserver(colId, "left", onHeaderSectionResize);
  const rightRef = useHeaderSectionObserver(colId, "right", onHeaderSectionResize);
  const columnLabel = (_a = columnMeta == null ? void 0 : columnMeta.label) != null ? _a : colId;
  const canSlice = columnMeta == null ? void 0 : columnMeta.enableSlicing;
  const isSliceActive = activeSliceCol === colId;
  const canSort = ctx.header.column.getCanSort();
  const sortDirection = ctx.header.column.getIsSorted();
  return /* @__PURE__ */ React.createElement(ThInner, { ref: setNodeRef }, /* @__PURE__ */ React.createElement(LeftCol, { ref: leftRef }, (columnMeta == null ? void 0 : columnMeta.enableColumnOrdering) && /* @__PURE__ */ React.createElement(
    DragHandle,
    {
      ref: setActivatorNodeRef,
      index: ctx.header.index,
      dragHandleProps: { ...attributes, ...listeners },
      isDragging,
      itemTitle: columnLabel
    }
  ), /* @__PURE__ */ React.createElement(
    HeaderActionsDropdown,
    {
      label: columnLabel,
      activeSort: sortDirection,
      isSliceActive,
      onChangeSlicing: canSlice ? onChangeSlicing : void 0,
      sliceColumnId: colId,
      appSection,
      onSort: canSort ? (desc) => ctx.table.setSorting([{ id: colId, desc }]) : void 0
    },
    (_d = (_c = (_b = columnMeta == null ? void 0 : columnMeta.columnRenderer) == null ? void 0 : _b.renderHeader) == null ? void 0 : _c.call(_b, columnLabel)) != null ? _d : columnLabel
  ), isSliceActive && /* @__PURE__ */ React.createElement(ActiveSliceIcon, { name: "surgical", title: `Slicing by ${columnLabel}`, size: "xs" }), sortDirection && /* @__PURE__ */ React.createElement(SortIcon, { column: ctx.header.column })), /* @__PURE__ */ React.createElement(RightCol, { ref: rightRef }, ctx.header.column.getCanResize() && /* @__PURE__ */ React.createElement(
    ResizeHandle,
    {
      onMouseDown: ctx.header.getResizeHandler(),
      onTouchStart: ctx.header.getResizeHandler(),
      colTitle: columnLabel
    }
  )));
};
const useAttributeColumnDefinitions = ({
  columnHelper,
  columnRenderersByAttribute,
  columnSchemas,
  columnWidths,
  entityAttributesAreCamelCase,
  enableSlicing,
  meta,
  onChangeSlicing,
  onHeaderSectionResize,
  appSection
}) => {
  const cell = useCallback(
    ({
      row,
      getValue,
      column
    }) => {
      var _a, _b, _c;
      const columnDefMeta = column.columnDef.meta;
      return (_c = (_b = (_a = columnDefMeta == null ? void 0 : columnDefMeta.columnRenderer) == null ? void 0 : _a.renderCell) == null ? void 0 : _b.call(_a, getValue(), row.original, meta)) != null ? _c : getValue();
    },
    [meta]
  );
  const header = useCallback(
    (ctx) => /* @__PURE__ */ React.createElement(
      AttributeHeader,
      {
        ctx,
        onHeaderSectionResize,
        onChangeSlicing,
        appSection
      }
    ),
    [appSection, onChangeSlicing, onHeaderSectionResize]
  );
  return useMemo(
    () => columnSchemas.map((col) => {
      var _a;
      const baseColDef = {
        id: col.id,
        cell,
        header,
        size: columnWidths[col.id],
        enableHiding: true,
        enableResizing: !columnRenderersByAttribute[col.id].staticWidth,
        meta: {
          label: col.title,
          enableSlicing: enableSlicing && col.sliceable,
          columnRenderer: columnRenderersByAttribute[col.id],
          enableColumnOrdering: true
        }
      };
      if (col.isDerived) {
        return columnHelper.display(baseColDef);
      }
      const attributeName = entityAttributesAreCamelCase ? camelCase(col.id) : col.id;
      return columnHelper.accessor((row) => row[attributeName], {
        enableSorting: (_a = col.sortable) != null ? _a : false,
        ...baseColDef
      });
    }),
    [
      columnSchemas,
      cell,
      header,
      columnWidths,
      columnRenderersByAttribute,
      enableSlicing,
      entityAttributesAreCamelCase,
      columnHelper
    ]
  );
};
export default useAttributeColumnDefinitions;
